import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { buttonVariants } from '@deepguide-ai/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@deepguide-ai/ui/dropdown-menu'
import { trpc } from '@/lib/trpc'
import { downloadFile } from '@/lib/download-file'

export function EvidencePdfDownload({ evidenceFileId, url, label }: { evidenceFileId: string; url: string; label?: string }) {
  const [jobId, setJobId] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const delivered = useRef<string | null>(null)
  const start = useMutation(trpc.evidenceFile.startPdfExport.mutationOptions({
    onSuccess: (result) => { delivered.current = null; setJobId(result.jobId) },
    onError: (error) => toast.error(error.message),
  }))
  const status = useQuery({
    ...trpc.evidenceFile.pdfExportStatus.queryOptions({ jobId: jobId ?? '' }),
    enabled: jobId !== null,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) => query.state.error || query.state.data?.state === 'completed' || query.state.data?.state === 'failed' ? false : 1500,
    retry: false,
  })
  useEffect(() => {
    if (!jobId || delivered.current === jobId) return
    if (status.error || status.data?.state === 'failed') {
      delivered.current = jobId
      toast.error(status.error?.message ?? (status.data?.state === 'failed' ? status.data.error : 'PDF export failed'))
      setJobId(null)
      return
    }
    if (status.data?.state !== 'completed') return
    delivered.current = jobId
    setDownloading(true)
    void downloadFile(status.data.downloadUrl, status.data.fileName)
      .catch((error: unknown) => toast.error(error instanceof Error ? error.message : 'PDF download failed'))
      .finally(() => { setDownloading(false); setJobId(null) })
  }, [jobId, status.data, status.error])
  const busy = start.isPending || jobId !== null || downloading
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type='button' title={busy ? 'Generating PDF…' : 'Download'} aria-label='Download evidence' disabled={busy} className={label ? buttonVariants({ variant: 'ghost', size: 'sm', className: 'h-8 shrink-0 text-[10px]' }) : 'inline-flex shrink-0 items-center gap-1 p-1 text-muted-foreground hover:text-foreground disabled:opacity-50'}>
          {busy ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Download className='h-3.5 w-3.5' />}
          {label && <span className='text-[10px]'>{label}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem asChild><a href={url} target='_blank' rel='noreferrer'>Download Markdown</a></DropdownMenuItem>
        <DropdownMenuItem onSelect={() => start.mutate({ evidenceFileId })}>Download PDF</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
